import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Button
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import ProfileCustomDialog from './ProfileCustomDialog';

interface Profile {
  id: string;
  name: string;
  description: string;
  environment: string;
  isActive: boolean;
  hasCredentials: boolean;
}

interface ProfileStats {
  totalProfiles: number;
  activeProfiles: number;
  customProfiles: number;
}

interface ProfileSwitcherProps {
  compact?: boolean;
}

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ compact = false }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/recup-profiles');
      
      if (response.data.success) {
        setProfiles(response.data.data.profiles);
        setActiveProfile(response.data.data.activeProfile);
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to load profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchProfile = async (profileId: string) => {
    if (switching || profileId === activeProfile?.id) return;

    try {
      setSwitching(true);
      const response = await apiClient.post('/recup-profiles/switch', {
        profileId
      });

      if (response.data.success) {
        setActiveProfile(response.data.data);
        setTestStatus('idle');
        
        // Ricarica la lista per aggiornare lo stato
        await loadProfiles();
      }
    } catch (error: any) {
      console.error('Failed to switch profile:', error);
      
      // Show user-friendly error message
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Cannot switch profile: ${errorMessage}`);
    } finally {
      setSwitching(false);
    }
  };

  const testConnection = async () => {
    if (!activeProfile) return;

    try {
      setTestStatus('testing');
      const response = await apiClient.get('/recup-profiles/test-connection');
      
      setTestStatus(response.data.success ? 'success' : 'error');
      
      // Reset status after 3 seconds
      setTimeout(() => setTestStatus('idle'), 3000);
    } catch (error) {
      console.error('Connection test failed:', error);
      setTestStatus('error');
      setTimeout(() => setTestStatus('idle'), 3000);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const getEnvironmentColor = (environment: string) => {
    switch (environment.toLowerCase()) {
      case 'production':
        return 'error';
      case 'staging':
        return 'warning';
      case 'test':
        return 'info';
      case 'default':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getTestIcon = () => {
    switch (testStatus) {
      case 'testing':
        return <CircularProgress size={16} />;
      case 'success':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <RefreshIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        {!compact && <span>Caricamento profili...</span>}
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <FormControl size="small" sx={{ minWidth: compact ? 120 : 200 }}>
        <InputLabel>Profilo RECUP</InputLabel>
        <Select
          value={activeProfile?.id || ''}
          onChange={(e) => switchProfile(e.target.value)}
          disabled={switching}
          label="Profilo RECUP"
        >
          {profiles.map((profile) => (
            <MenuItem 
              key={profile.id} 
              value={profile.id}
              disabled={!profile.isActive}
            >
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <span style={{ 
                  color: profile.isActive ? 'inherit' : '#999',
                  textDecoration: profile.isActive ? 'none' : 'none'
                }}>
                  {profile.name}
                </span>
                <Chip 
                  label={profile.environment}
                  size="small"
                  color={getEnvironmentColor(profile.environment) as any}
                  variant="outlined"
                />
                {!profile.isActive && (
                  <Chip 
                    label="No Credentials"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
                {profile.isActive && profile.hasCredentials && (
                  <Chip 
                    label="Ready"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {activeProfile && (
        <Tooltip title={
          testStatus === 'testing' ? 'Testing connection...' :
          testStatus === 'success' ? 'Connection successful' :
          testStatus === 'error' ? 'Connection failed' :
          'Test connection'
        }>
          <IconButton
            size="small"
            onClick={testConnection}
            disabled={testStatus === 'testing'}
          >
            {getTestIcon()}
          </IconButton>
        </Tooltip>
      )}

      {switching && (
        <CircularProgress size={16} />
      )}

      {!compact && (
        <Tooltip title="Crea profilo personalizzato">
          <IconButton
            size="small"
            onClick={() => setShowCustomDialog(true)}
            disabled={loading}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {!compact && stats && (
        <Tooltip title={`Total: ${stats.totalProfiles}, Active: ${stats.activeProfiles}, Custom: ${stats.customProfiles}`}>
          <Chip 
            label={`${stats.activeProfiles}/${stats.totalProfiles}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Tooltip>
      )}

      <ProfileCustomDialog
        open={showCustomDialog}
        onClose={() => setShowCustomDialog(false)}
        onProfileCreated={loadProfiles}
      />
    </Box>
  );
};

export default ProfileSwitcher;